import { Icon, Typography } from "@mindlercare/ui-web";
import { defaultTheme } from "@mindlercare/ui-web/src/shared/constants";
import { useEffect, useRef } from "react";
import { styled } from "styled-components";
import { useIcbtSelector } from "../IcbtSelectorContext";
import { useIcbtTranslation } from "../translations";
import { List, ListItemSubtitle, ModalContentColumn } from "../common";
import type { MappedTreatment } from "../types";

type TreatmentColumnProps = {
  treatments: MappedTreatment[];
  handleSelectTreatment: (treatmentId?: string) => void;
  programsToAssign: Record<string, string[]>;
  isPreviewOnly?: boolean;
};

const TreatmentColumn = ({
  treatments,
  handleSelectTreatment,
  programsToAssign,
  isPreviewOnly,
}: TreatmentColumnProps) => {
  const { selectedTreatmentId } = useIcbtSelector();
  const selectedRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    selectedRef.current?.scrollIntoView({ block: "nearest" });
  }, [selectedTreatmentId]);

  return (
    <ModalContentColumn>
      <List>
        {treatments.map((singleTreatment) => (
          <ListItem
            key={singleTreatment.id}
            onClick={() => {
              handleSelectTreatment(singleTreatment.id);
            }}
            selected={selectedTreatmentId === singleTreatment.id}
            ref={
              selectedTreatmentId === singleTreatment.id ? selectedRef : null
            }
          >
            <ListItemInnerContainer>
              <TreatmentTextContainer>
                <Typography
                  insensitive
                  variant="body"
                  className="treatment-title"
                >
                  {singleTreatment.title}
                </Typography>
                {!isPreviewOnly && (
                  <TreatmentStatus
                    treatmentHasProgramAssigned={singleTreatment.programs.some(
                      ({ assigned }) => assigned,
                    )}
                  />
                )}
              </TreatmentTextContainer>
              <NumberAndIconContainer>
                {programsToAssign[singleTreatment.id]?.length > 0 && (
                  <Typography insensitive variant="body" color="GRAY">
                    {programsToAssign[singleTreatment.id].length}
                  </Typography>
                )}
                <Icon.ArrowRight />
              </NumberAndIconContainer>
            </ListItemInnerContainer>
          </ListItem>
        ))}
      </List>
    </ModalContentColumn>
  );
};

export default TreatmentColumn;

export const ListItem = styled.div<{ selected: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 8px 16px;
  align-items: center;
  cursor: pointer;
  width: auto;
  &:hover {
    background-color: ${({ theme }) => theme.COLORS.GRAY_X_LIGHT};
    .treatment-title {
      text-decoration: underline;
    }
  }
  border-radius: 2px;
  background-color: ${({ selected, theme }) =>
    selected ? theme.COLORS.LIGHT_ACTION_BLUE : "inherit"};
`;

const TreatmentStatusContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding-top: 4px;
  gap: 4px;
`;

const ListItemInnerContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  gap: 24px;
  justify-content: space-between;
  align-items: center;
`;

const TreatmentTextContainer = styled.div`
  overflow: hidden;

  .treatment-title {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
`;

const NumberAndIconContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  align-items: center;
`;

// --- Helpers ---

const TreatmentStatus = ({
  treatmentHasProgramAssigned,
}: {
  treatmentHasProgramAssigned: boolean;
}) => {
  const t = useIcbtTranslation();

  if (!treatmentHasProgramAssigned) return null;

  return (
    <TreatmentStatusContainer>
      <Icon.Assignment style={{ color: defaultTheme.COLORS.GRAY }} />
      <ListItemSubtitle>{t.PROGRAMS_ASSIGNED}</ListItemSubtitle>
    </TreatmentStatusContainer>
  );
};
